/**
 * Custom Wishlist Implementation
 * - LocalStorage based
 * - Syncs across tabs/pages
 * - Updates header badge
 */

const WISHLIST_KEY = 'kitchen-pro-wishlist';

class WishlistManager {
    constructor() {
        this.items = this.loadItems();
        this.init();
    }

    init() {
        this.updateUI();
        this.bindEvents();

        // Listen for storage changes (cross-tab sync)
        window.addEventListener('storage', (e) => {
            if (e.key === WISHLIST_KEY) {
                this.items = this.loadItems();
                this.updateUI();
            }
        });

        // Custom event for internal updates
        document.addEventListener('wishlist:updated', () => {
            this.updateUI();
        });
    }

    loadItems() {
        try {
            return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
        } catch (e) {
            console.error('Wishlist load error:', e);
            return [];
        }
    }

    saveItems() {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(this.items));
        document.dispatchEvent(new CustomEvent('wishlist:updated'));
    }

    toggleItem(product) {
        const index = this.items.findIndex(item => item.handle === product.handle);

        if (index === -1) {
            this.items.push(product);
        } else {
            this.items.splice(index, 1);
        }

        this.saveItems();
    }

    isInWishlist(handle) {
        return this.items.some(item => item.handle === handle);
    }

    remove(handle) {
        const index = this.items.findIndex(item => item.handle === handle);
        if (index > -1) {
            this.items.splice(index, 1);
            this.saveItems();
        }
    }

    bindEvents() {
        document.body.addEventListener('click', (e) => {
            const btn = e.target.closest('.wishlist-btn');
            if (!btn) return;

            e.preventDefault();

            const product = {
                handle: btn.dataset.wishlistHandle,
                id: btn.dataset.wishlistId,
                title: btn.dataset.wishlistTitle,
                image: btn.dataset.wishlistImage,
                price: btn.dataset.wishlistPrice,
                url: btn.dataset.wishlistUrl
            };

            this.toggleItem(product);
        });
    }

    updateUI() {
        // Update Buttons
        const buttons = document.querySelectorAll('.wishlist-btn');
        buttons.forEach(btn => {
            const handle = btn.dataset.wishlistHandle;
            const isActive = this.isInWishlist(handle);

            btn.classList.toggle('active', isActive);
            const icon = btn.querySelector('svg');
            if (icon) {
                if (isActive) {
                    icon.setAttribute('fill', 'currentColor');
                    icon.classList.add('text-red-500');
                    icon.classList.remove('text-gray-400');
                } else {
                    icon.setAttribute('fill', 'none');
                    icon.classList.remove('text-red-500');
                    icon.classList.add('text-gray-400');
                }
            }
        });

        // Update Header Badge
        const badge = document.querySelector('[data-wishlist-count]');
        if (badge) {
            badge.textContent = this.items.length;
            badge.classList.toggle('hidden', this.items.length === 0);
        }

        // Render Page if on Wishlist Page
        const grid = document.getElementById('WishlistGrid');
        if (grid) {
            this.renderWishlistPage(grid);
        }
    }

    renderWishlistPage(grid) {
        if (this.items.length === 0) {
            grid.innerHTML = `
        <div class="col-span-full text-center py-12">
          <p class="text-gray-500 mb-4">Your wishlist is empty.</p>
          <a href="/collections/all" class="btn btn--primary">Continue Shopping</a>
        </div>
      `;
            return;
        }

        grid.innerHTML = this.items.map(item => `
      <div class="product-card group relative">
        <div class="aspect-square overflow-hidden rounded bg-gray-100 mb-3 relative">
           <a href="${item.url}">
            <img src="${item.image}" alt="${item.title}" class="w-full h-full object-cover">
           </a>
           <button class="wishlist-btn active absolute top-2 right-2 z-10 p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow" 
                data-wishlist-handle="${item.handle}">
                <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
           </button>
        </div>
        <h3 class="font-medium text-lg leading-tight mb-1">
          <a href="${item.url}" class="hover:underline">${item.title}</a>
        </h3>
        <div class="text-gray-600">
          <span>${item.price}</span>
        </div>
      </div>
    `).join('');

        // Re-bind events for newly added buttons (delegation handles click, but UI update needs to run)
        // Actually delegation in bindEvents handles the click, updateUI handles the view.
    }
}

document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
});
